import React, { useEffect } from 'react';
import { IonButton, IonContent, IonText } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { RevenueCatModel } from './revenue-cat/RevenueCatModel.ts';
import { PaywallDialogViewModel } from './PaywallDialogViewModel.tsx';
import { PaymentTermButton } from './PaymentTermsButton.tsx';
import { TrackingService } from '../support/tracking/TrackingService.ts';
import { formatDollarsAndCents } from '../support/helpers/format.ts';

export const PromotionDialogContent: React.FC<{onUpgrade: () => void, onRestore: () => void}> = ({ onUpgrade, onRestore }) => {

    const tracking = useInstance(TrackingService);

    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    const dialogVM = useInstance(PaywallDialogViewModel);
    useSubscription(() => dialogVM);

    const promotion = dialogVM.getPromotionConfig();
    const term = dialogVM.state.paymentTerm;

    // NOTE: check these prices match those in RevenueCat dashboard
    const monthlyPrice = 3.99;
    const annualPrice = 39.99;

    const promotionPrice = (annualPrice + .01) * (1 - promotion.savingPct/100) - .01;

    const buttonMessage = (term === 'annual' ? `Renews at $${annualPrice} after 1 year, cancel any time` : 'Cancel any time');
    const buttonText = (term === 'annual' ? 'Claim Limited Offer' : 'Continue');

    const formatPrice = (price: number) => {
        const formatted = formatDollarsAndCents(price);
        return (formatted.endsWith('.00') ? formatted.substring(0, formatted.length - 3) : formatted);
    };

    useEffect(() => {
        if (!revenueCat.hasUpgraded()) {
            tracking.track('Paywall Opened', () => ({
                from: dialogVM.getTriggerSource(),
                paywallTitle: promotion.title,
                annualPrice: promotionPrice,
                monthlyPrice,
                buttonText
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onContinue = () => {
        tracking.track('Paywall Continued', () => ({
            term,
            from: dialogVM.getTriggerSource(),
            paywallTitle: promotion.title,
            annualPrice: promotionPrice,
            monthlyPrice,
            buttonText
        }));
        onUpgrade();
    };

    return (
        <IonContent style={{'--background': promotion.background}}>
            <div style={{height: '100%', maxWidth: 390, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-around', padding: 'calc(var(--ion-safe-area-top) + 25px) 20px max(var(--ion-safe-area-bottom), 25px)'}}>

                <img style={{padding: '10px 55px', minHeight: 0, flex: '100 100 0'}} src={`./assets/images/paywall/${promotion.image}`} alt={promotion.title} />

                <div style={{width: '100%', flex: '5 0 auto'}}>
                    <PaymentTermButton
                        disabled={dialogVM.state.isWorking}
                        isActive={term === 'annual'}
                        onClick={() => dialogVM.setState({ paymentTerm: 'annual' })}
                        termLabel={`${promotion.title} 🎁`}
                        termDescription={<>1 year <s>{formatPrice(annualPrice)}</s> {formatPrice(promotionPrice)} (only {formatPrice(promotionPrice/12)}/month)</>}
                        badge={`SAVE ${promotion.savingPct}%`}
                        badgeBackground={promotion.badgeBackground}
                        badgeTextColor={promotion.badgeTextColor}
                    />

                    <PaymentTermButton
                        disabled={dialogVM.state.isWorking}
                        isActive={term === 'monthly'}
                        onClick={() => dialogVM.setState({ paymentTerm: 'monthly' })}
                        termLabel={`Monthly Plan — ${formatPrice(monthlyPrice)}`}
                        termDescription={`Trial, then ${formatPrice(monthlyPrice)}, billed monthly`}
                    />
                </div>

                <div style={{width: '100%', flex: '0 0 auto'}}>
                    <IonButton
                        disabled={dialogVM.state.isWorking}
                        expand="block"
                        fill="solid"
                        color="primary"
                        onClick={() => onContinue()}
                        data-id="continue"
                    >
                        <span>{buttonText}</span>
                    </IonButton>

                    <div style={{width: '100%', textAlign: 'center'}}>
                        <IonText color="medium"><small>{buttonMessage}</small></IonText>
                    </div>

                    <div style={{width: '100%', maxWidth: 260, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <IonButton disabled={dialogVM.state.isWorking} size="small" expand="block" fill="clear" href="https://www.autopylot.io/terms" target="_blank">Terms of Use</IonButton>
                        {revenueCat.canRestore() &&
                            <IonButton expand="block" size="small" fill="clear" onClick={onRestore} data-id="restore">Restore Purchases</IonButton>
                        }
                    </div>
                </div>

            </div>
        </IonContent>
    );
};
